import { PropsWithChildren } from 'react';
import Image from 'next/image';

import { Box, Divider, Grid, GridItem, Heading, SimpleGrid } from '@chakra-ui/react';

import { Card } from '~/components/card';
import { ContentContainer } from '~/components/container';

import { PersonalizedDiscountHero } from './personalized-discount-hero';
import { UdacityBenefitsSection } from './udacity-benefits-section';

const trustedCompaniesList = [
  { imageSrc: '/images/companies/aws.svg', label: 'AWS Image' },
  { imageSrc: '/images/companies/accenture.svg', label: 'Accenture Image' },
  { imageSrc: '/images/companies/bnp.svg', label: 'BNP Image' },
  { imageSrc: '/images/companies/shell.svg', label: 'Shell Image' },
  { imageSrc: '/images/companies/toyota.svg', label: 'Toyota Image' },
  { imageSrc: '/images/companies/cisco.svg', label: 'Cisco Image' },
  { imageSrc: '/images/companies/airbus.svg', label: 'Airbus Image' },
  { imageSrc: '/images/companies/vodafone.svg', label: 'Vodafone Image' },
] as const;

/** Marketing shell for `/get-personalized-discount`; the lead form renders inside the card. */
export function GetPersonalizedDiscountLayout({ children }: PropsWithChildren) {
  return (
    <>
      <PersonalizedDiscountHero />

      <Box backgroundColor='accent.sky.100'>
        <ContentContainer>
          <Grid
            columnGap={{ base: '32px', xl: '64px' }}
            gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }}
            gridTemplateRows={{ base: '1fr', md: '1fr min-content' }}
            paddingY={{ base: '32px', md: '72px' }}
            templateAreas={{
              base: `"whyUdacitySection"
                "form" 
                "companiesSection"`,

              md: `"whyUdacitySection form"
                  "companiesSection form"`,
            }}
          >
            <GridItem area={'whyUdacitySection'}>
              <UdacityBenefitsSection />
            </GridItem>

            <GridItem area={'companiesSection'}>
              <Divider marginY='32px' />

              <Heading
                as='p'
                color='blue.400'
                size='marketing-md'
                textAlign={{
                  base: 'center',
                  md: 'left',
                }}
              >
                Trusted by the world&apos;s leading companies
              </Heading>

              <SimpleGrid marginTop='16px' minChildWidth='120px' spacing='16px'>
                {trustedCompaniesList.map((trustedCompany) => (
                  <Box
                    key={trustedCompany.label}
                    alignItems='center'
                    display='flex'
                    height='56px'
                    justifyContent='center'
                  >
                    <Image
                      key={trustedCompany.label}
                      alt={trustedCompany.label}
                      height={56}
                      src={trustedCompany.imageSrc}
                      style={{ height: '56px', objectFit: 'scale-down' }}
                      width={120}
                    />
                  </Box>
                ))}
              </SimpleGrid>
            </GridItem>

            <GridItem area={'form'} marginTop={{ base: '24px', lg: '0px' }}>
              <Card
                margin='auto'
                maxWidth={{ base: '568px', lg: 'unset' }}
                padding={{
                  base: '24px',
                  xl: '40px',
                }}
              >
                {children}
              </Card>
            </GridItem>
          </Grid>
        </ContentContainer>
      </Box>
    </>
  );
}
